import React, { useState, useEffect, useCallback } from 'react';
import { useStaticQuery, graphql } from "gatsby"
import styled from '@emotion/styled';
import { GatsbyImage, getImage, withArtDirection } from "gatsby-plugin-image";
import * as styleConfig from '../../../styles/config.module.scss';
import { useLocalization } from "gatsby-theme-i18n";
import IngameItem from "./IngameItem";
import { parseHTML } from '../../../functions/common'
import * as breakpoint from '../../../styles/breakpoints.module.scss';
import Notification from "./Notification";

const MainVisual = () => {

  const { t } = useLocalization();
  const [ mainVisualLoaded, setMainVisualLoaded ] = useState(false);
  const [ componentLoaded, setComponentLoaded ] = useState(false);

  const data = useStaticQuery(graphql`
    query MainVisual {
      mainVisual : file(sourceInstanceName: {eq: "images"}, relativePath: {eq: "layout/mainVisual.jpg"}) {
        childImageSharp {
          gatsbyImageData(
            quality: 80
            layout: FULL_WIDTH
            placeholder: NONE
          )
        }
      }
      mainVisual_tablet : file(sourceInstanceName: {eq: "images"}, relativePath: {eq: "layout/mainVisual_tablet.jpg"}) {
        childImageSharp {
          gatsbyImageData(
            quality: 80
            layout: FULL_WIDTH
            placeholder: NONE
          )
        }
      }
      mainVisual_mobile : file(sourceInstanceName: {eq: "images"}, relativePath: {eq: "layout/mainVisual_mobile.jpg"}) {
        childImageSharp {
          gatsbyImageData(
            quality: 80
            layout: FULL_WIDTH
            placeholder: NONE
          )
        }
      }
    }
  `)

  const heroTitle = () => {
    if(t('home.hero.title') === "")
      return "";
    let str = t('home.hero.title').replace('%highlight%',`<span class="red">${t('home.hero.title.highlight')}</span>`)
    return `${str.split('\n')[0]} <span class="nolinebreak">${str.split('\n')[1]}</span>`;
  };

  const checkLoaded = useCallback(() => {
    if(document)
    {
      if(document.querySelectorAll(`#mainVisualWrapper .mainVisual img`).length > 0 && document.querySelectorAll(`#mainVisualWrapper .mainVisual img`)[0].complete)
      {
        setMainVisualLoaded(true);
      } else if(!mainVisualLoaded) {
        setTimeout(checkLoaded,50);
      }
    }
  },[mainVisualLoaded, setMainVisualLoaded]);

  useEffect(() => {
    checkLoaded();
  },[checkLoaded])


  return(
    <SectionWrapper className={mainVisualLoaded && componentLoaded ? 'loaded' : ''}>
      <Notification />
      <MainVisualWrapper id="mainVisualWrapper">
        <GatsbyImage className="mainVisual" image={
          withArtDirection(getImage(data.mainVisual), [
            {
              media: `(min-width: ${breakpoint.mdMin})`,
              image: getImage(data.mainVisual),
            },
            {
              media: `(max-width: ${breakpoint.smMax}) and (min-width: ${breakpoint.smMin})`,
              image: getImage(data.mainVisual_tablet),
            },
            {
              media: `(max-width: ${breakpoint.xsMax})`,
              image: getImage(data.mainVisual_mobile),
            }
          ])} onLoad={checkLoaded} alt={t('home.hero.catch')}/>
        <MainHeadlineWrapper>
          <h1>{parseHTML(heroTitle())}</h1>
          <div>{t('home.hero.catch')}</div>
          <div>{t('home.hero.sub_title')}</div>
        </MainHeadlineWrapper>

        <DatetimeWrapper>
          <div>
            {parseHTML(t('home.hero.event_date')
              .replace('%sat%',`<span class="blue">${t('home.hero.event_date.sat')}</span>`)
              .replace('%sun%',`<span class="red">${t('home.hero.event_date.sun')}</span>`)
            )}
            </div>
          <div>{t('home.hero.venue')}</div>
        </DatetimeWrapper>
      </MainVisualWrapper>
      <BottomInformationWrapper>
        <IngameItem componentLoaded={() => {
          setComponentLoaded(true);
        }}>
          <span>{t('home.badge.label')}</span>
          <span>{t('home.badge.title.top')}</span>
          <span>{t('home.badge.title.bottom')}</span>
          <span>{t('home.badge.status')}</span>
        </IngameItem>
      </BottomInformationWrapper>
    </SectionWrapper>);
}

export default MainVisual;

const SectionWrapper = styled.div`

  z-index: 3;
  position: relative;
  padding-bottom:176px;

  @media only screen and (max-width: ${breakpoint.smMax}){
    padding-bottom:96px;
  }

  @media only screen and (max-width: ${breakpoint.xsMax}){
    padding-bottom:88px;
  }
`

const MainVisualWrapper = styled.div`
  width: ${styleConfig.layoutWidthXL};
  max-width:calc(100% - 160px);
  height:784px;
  margin:0 auto;
  border-radius: 24px 24px 48px 48px;
  overflow:hidden;
  box-shadow: 0px 6px 4px -4px rgba(0, 0, 0, 0.1), 0px 12px 8px -8px rgba(0, 0, 0, 0.05), 0px 24px 16px -16px rgba(0, 0, 0, 0.05);
  position:relative;
  background-color:#141830;
  z-index:0;

  >.gatsby-image-wrapper{
    height: 100%;
    width: 100%;
    opacity:0;

    .loaded &{
      opacity:1;
    }
  }

  @media only screen and (max-width: ${breakpoint.smMax}){
    height:calc((100vw - 160px) * 1.28);
  }

  @media only screen and (max-width: ${breakpoint.xsMax}){
    max-width:calc(100% - 48px);
    height:480px;

  }
`


const MainHeadlineWrapper = styled.div`
  position:absolute;
  bottom:50%;
  margin-bottom:-56px;
  
  left:0%;
  width: 100%;
  padding:0 48px;

  > h1{
    word-break: keep-all;
    font-weight: 900;
    font-size: 40px;
    line-height: 48px;
    color: #FFFFFF;
    text-shadow: 0px 0px 12px rgba(64, 77, 175, 0.5);
    margin-bottom:24px;
    letter-spacing: 0.05em;
    text-align:center;

    .red{
      color: #E60D33;
      // font-size:24px;
    }

    opacity:0;
    transform:translateY(30px);
    transition:transform .5s, opacity .5s;

    .loaded &{
      opacity:1;
      transform:none;
    }
  }

  > div:nth-of-type(1){
    font-weight: 900;
    font-size:96px;
    html[lang="ja"] &{
      font-size: 120px;
    }
    
    line-height: 120px;
    
    margin-bottom: 24px;

    color: #FAFAFE;
    text-shadow: 0px 1px 24px rgba(134, 131, 255, 0.4);
    text-align:center;
    opacity:0;
    transform:translateY(30px);
    transition:transform .5s, opacity .5s;
    transition-delay:.3s;

    .loaded &{
      opacity:1;
      transform:none;
    }
  }

  > div:nth-of-type(2){
    font-size: 24px;
    font-weight: 900;
    line-height: 32px;

    text-align: center;
    letter-spacing: 0.3em;

    color: #FFFFFF;

    text-shadow: 0px 0px 12px rgba(64, 77, 175, 0.5);
    margin-right: -0.5em;
    opacity:0;
    transform:translateY(30px);
    transition:transform .5s, opacity .5s;
    transition-delay:.6s;

    .loaded &{
      opacity:1;
      transform:none;
    }
  }


  @media only screen and (max-width: 1280px){
    > div:nth-of-type(1){
      font-size:71px;
      html[lang="ja"] &{
        font-size:100px;
      }
    }
  }

  @media only screen and (max-width: ${breakpoint.smMax}){
    padding:22px 48px;
    top:27%;
    > h1{
      font-size: 32px;
      line-height: 40px;
      height:auto;
    }
    > div:nth-of-type(1){
      font-size: 64px !important;
      line-height: 100%;
    }
    > div:nth-of-type(2){
      font-size: 16px;
      line-height: 24px;
    }
  }

  @media only screen and (max-width: ${breakpoint.xsMax}){
    padding:0px 24px;
    top:25.8%;

    > h1{
      font-size: 16px;
      line-height: 28px;
      margin-bottom:8px;
      letter-spacing: 0;
    }
    > div:nth-of-type(1){
      font-size: 31px !important;
      line-height: 32px;
      margin-bottom:8px;
    }
    > div:nth-of-type(2){
      font-size: 12px;
      line-height: 24px;
    }
  }
`

const DatetimeWrapper = styled.div`
  position:absolute;
  bottom:14.8%;
  left:50%;
  width:100%;
  padding:0 48px;

  opacity:0;
  transform:translate(-50%,30px);
  transition:all .5s;
  transition-delay:.9s;

  .loaded &{
    opacity:1;
    transform:translate(-50%,0px);
  }
 

  >div:nth-of-type(1){
    font-weight: 900;
    font-size: 24px;
    line-height: 32px;
    text-align:center;

    letter-spacing: 0.025em;
    color: #FAFAFE;
    text-shadow: 0px 0px 24px rgba(0, 0, 0, 0.1);

    .red{
      color:#E51C3B
    }
    .blue{
      color:#039BE5
    }

    @media only screen and (max-width: 1660px) and (min-width: ${breakpoint.mdMin}){
      width: 450px;
      margin: 0 auto 3px;
    }
  }
  >div:nth-of-type(2){
    font-size: 14px;
    line-height: 20px;
    text-align:center;


    color: #ECECF0;
    text-shadow: 0px 0px 24px rgba(0, 0, 0, 0.1);
  
  }

  @media only screen and (max-width: ${breakpoint.smMax}){
    bottom:auto;
    top:60.2%;
    margin-top:48px;
  }

  @media only screen and (max-width: ${breakpoint.xsMax}){
    bottom:18.3%;
    top:auto;
    padding:0px;
    >div:nth-of-type(1){
      font-size: 16px;
      line-height: 24px;
      letter-spacing:0; 
    }

    >div:nth-of-type(2){
      font-size: 12px;
      line-height: 20px;
      letter-spacing:0;
    }
    
    max-width:calc(100% - 48px);
    
  }
`

const BottomInformationWrapper = styled.div`
  width: ${styleConfig.layoutWidthXL};
  margin:0 auto;
  display:flex;
  justify-content:flex-end;
  max-width:calc(100% - 160px);

  @media only screen and (max-width: ${breakpoint.xsMax}){
    max-width:calc(100% - 48px);
  }
`
